import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Radar, Clock, MousePointerClick, Wand2, Send, Loader2, Zap, RefreshCw, Trash2, CheckCircle2, ExternalLink, PenLine, Bell, Play, Mail } from "lucide-react";
import { toast } from "sonner";
import SignalRulesPanel from "@/components/SignalRulesPanel";
import SignalReplay from "@/components/SignalReplay";
import SignalDigest from "@/components/SignalDigest";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function intentBucket(score) {
  if (score >= 90) return { label: "Red hot", cls: "bg-rose-500/15 text-rose-300 border-rose-400/20" };
  if (score >= 80) return { label: "Hot", cls: "bg-amber-500/15 text-amber-300 border-amber-400/20" };
  return { label: "Warm", cls: "bg-sky-500/15 text-sky-300 border-sky-400/20" };
}

export default function Signals() {
  const [signals, setSignals] = useState([]);
  const [active, setActive] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [replayOpen, setReplayOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);

  const load = async (keepActiveId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/signals");
      setSignals(data);
      if (keepActiveId) {
        const found = data.find((s) => s.signal_id === keepActiveId);
        setActive(found || data[0] || null);
      } else if (!active && data.length) {
        setActive(data[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const simulate = async () => {
    setSimulating(true);
    try {
      const { data } = await api.post("/signals/simulate");
      toast.success(`New visit: ${data.visitor_name} @ ${data.company}`);
      await load(data.signal_id);
      setActive(data);
    } catch {
      toast.error("Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  const draftReply = async () => {
    if (!active) return;
    setDrafting(true);
    try {
      const { data } = await api.post(`/signals/${active.signal_id}/draft`);
      setActive({ ...active, ai_draft: data.draft, status: "drafted" });
      setSignals((prev) => prev.map((s) => (s.signal_id === active.signal_id ? { ...s, ai_draft: data.draft, status: "drafted" } : s)));
      toast.success("AI email drafted");
    } catch {
      toast.error("Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  const sendNow = async () => {
    if (!active?.ai_draft) return;
    try {
      const { data } = await api.post(`/signals/${active.signal_id}/send`);
      setActive(data);
      setSignals((prev) => prev.map((s) => (s.signal_id === data.signal_id ? data : s)));
      toast.success(`Sent to ${data.visitor_email}`);
    } catch {
      toast.error("Send failed");
    }
  };

  const removeSignal = async (id) => {
    await api.delete(`/signals/${id}`);
    const next = signals.filter((s) => s.signal_id !== id);
    setSignals(next);
    if (active?.signal_id === id) setActive(next[0] || null);
  };

  const stats = {
    total: signals.length,
    hot: signals.filter((s) => s.intent_score >= 90).length,
    sent: signals.filter((s) => s.sent).length,
    newLast15: signals.filter((s) => (Date.now() - new Date(s.visited_at).getTime()) < 15 * 60 * 1000).length,
  };

  return (
    <div className="space-y-6" data-testid="signals-view">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-300 mb-1">
            <Radar className="w-3.5 h-3.5" /> Inbound signals · live
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Pricing page visitors</h1>
          <p className="mt-1 text-sm text-slate-400 max-w-2xl">
            The moment a known prospect lands on your pricing page, we surface them here and pre-draft the follow-up.
            Reply within 5 minutes and conversion jumps 21×.
          </p>
        </div>
        <div className="flex gap-2">
          <SignalRulesPanel onRulesChanged={() => load(active?.signal_id)} />
          <Button onClick={() => setDigestOpen(true)} variant="outline" size="sm" data-testid="signals-digest-btn" className="h-9 border-white/10 bg-white/[0.03]">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Digest
          </Button>
          <Button onClick={() => load()} variant="outline" size="sm" data-testid="signals-refresh-btn" className="h-9 border-white/10 bg-white/[0.03]">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button onClick={simulate} disabled={simulating} size="sm" data-testid="signals-simulate-btn" className="h-9 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
            {simulating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Simulating…</> : <><Zap className="w-3.5 h-3.5 mr-1.5" /> Simulate visit</>}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3" data-testid="signals-stats">
        {[
          { label: "Live visits", value: stats.total, color: "text-white" },
          { label: "Red hot (90+)", value: stats.hot, color: "text-rose-300" },
          { label: "Sent replies", value: stats.sent, color: "text-emerald-300" },
          { label: "New · last 15m", value: stats.newLast15, color: "text-sky-300" },
        ].map((k) => (
          <div key={k.label} className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
            <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500">{k.label}</div>
            <div className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 min-h-[560px]">
        {/* List */}
        <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
          <div className="px-4 py-2.5 border-b border-white/5 text-[10px] uppercase font-mono tracking-widest text-slate-500">
            Recent visits
          </div>
          <div className="max-h-[560px] overflow-y-auto">
            {signals.length === 0 && !loading && (
              <div className="p-6 text-sm text-slate-500 text-center">No visits yet. Simulate one above.</div>
            )}
            {signals.map((s) => {
              const bucket = intentBucket(s.intent_score);
              return (
                <button
                  key={s.signal_id}
                  onClick={() => setActive(s)}
                  data-testid={`signals-thread-${s.signal_id}`}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition ${active?.signal_id === s.signal_id ? "bg-white/[0.04]" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{s.visitor_name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.visitor_title} · {s.company}</div>
                    </div>
                    {s.sent ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-sky-400 mt-1.5 shrink-0" />
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] font-mono ${bucket.cls}`}>{bucket.label} · {s.intent_score}</Badge>
                    {s.auto_sent && <Badge variant="outline" className="text-[10px] font-mono bg-rose-500/15 text-rose-300 border-rose-400/20"><Zap className="w-2.5 h-2.5 mr-0.5" />Auto-sent</Badge>}
                    {!s.auto_sent && s.auto_drafted && <Badge variant="outline" className="text-[10px] font-mono bg-sky-500/15 text-sky-300 border-sky-400/20"><PenLine className="w-2.5 h-2.5 mr-0.5" />Auto-drafted</Badge>}
                    {s.slack_notified && <Badge variant="outline" className="text-[10px] font-mono bg-violet-500/15 text-violet-300 border-violet-400/20"><Bell className="w-2.5 h-2.5 mr-0.5" />Slack</Badge>}
                    <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {timeAgo(s.visited_at)}</span>
                    <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1"><MousePointerClick className="w-2.5 h-2.5" /> {s.dwell_seconds}s</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 rounded-2xl border border-white/5 bg-white/[0.02] p-6">
          {active ? (
            <>
              <div className="flex items-start justify-between gap-4 pb-4 border-b border-white/5">
                <div>
                  <div className="text-lg font-semibold" data-testid="signals-detail-name">{active.visitor_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {active.visitor_title} · {active.company} · <a href={`mailto:${active.visitor_email}`} className="text-sky-300 hover:underline">{active.visitor_email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={intentBucket(active.intent_score).cls}>Intent {active.intent_score}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setReplayOpen(true)} data-testid="signals-replay-btn" className="h-8 border-white/10 bg-white/[0.02] text-xs">
                    <Play className="w-3 h-3 mr-1" /> Replay
                  </Button>
                  <button onClick={() => removeSignal(active.signal_id)} data-testid="signals-delete-btn" className="text-slate-500 hover:text-rose-300 p-1.5 rounded-lg hover:bg-white/[0.04]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
                {[
                  ["Page", active.page_label],
                  ["Dwell", `${active.dwell_seconds}s`],
                  ["Revisits", active.revisit_count],
                  ["Region", active.region],
                  ["Industry", active.industry],
                  ["Employees", active.employees],
                  ["Tech", (active.tech_stack || []).slice(0, 2).join(", ")],
                  ["Signal", active.recent_signal],
                ].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
                    <div className="text-[10px] uppercase font-mono tracking-widest text-slate-500">{k}</div>
                    <div className="text-slate-200 mt-0.5 truncate">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl border border-dashed border-white/10 p-4" data-testid="signals-draft-panel">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase font-mono tracking-widest text-emerald-300 flex items-center gap-1">
                    <Wand2 className="w-3 h-3" /> AI inbound reply
                  </div>
                  {!active.ai_draft && (
                    <Button size="sm" onClick={draftReply} disabled={drafting} data-testid="signals-draft-btn" className="h-7 text-xs bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold">
                      {drafting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Drafting…</> : "Draft email"}
                    </Button>
                  )}
                </div>

                {active.ai_draft ? (
                  <>
                    <div className="text-[11px] uppercase font-mono tracking-widest text-slate-500 mb-1">Subject</div>
                    <div className="text-sm text-slate-100 mb-3" data-testid="signals-draft-subject">{active.ai_draft.subject}</div>
                    <div className="text-[11px] uppercase font-mono tracking-widest text-slate-500 mb-1">Body</div>
                    <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap" data-testid="signals-draft-body">{active.ai_draft.body}</div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button size="sm" onClick={sendNow} disabled={active.sent} data-testid="signals-send-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold h-8">
                        {active.sent ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Sent</> : <><Send className="w-3 h-3 mr-1" /> Send now</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={draftReply} disabled={drafting} className="border-white/10 bg-white/[0.02] h-8">
                        {drafting ? "Regenerating…" : "Regenerate"}
                      </Button>
                      <a href={`mailto:${active.visitor_email}?subject=${encodeURIComponent(active.ai_draft.subject)}&body=${encodeURIComponent(active.ai_draft.body)}`} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white px-3 h-8 rounded-md border border-white/10 bg-white/[0.02]">
                        Open in mail <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-slate-400">
                    Hit <strong>Draft email</strong> — Claude Sonnet 4.6 writes a hyper-personalized inbound reply grounded in this visit + enrichment.
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 py-16 text-sm">
              {loading ? "Loading signals…" : "Select a visit from the left."}
            </div>
          )}
        </div>
      </div>

      <SignalReplay signal={active} open={replayOpen} onOpenChange={setReplayOpen} />
      <SignalDigest open={digestOpen} onOpenChange={setDigestOpen} />
    </div>
  );
}
